@if (variant() === 'flat') {
  <div class="space-y-6">
    <ng-container *ngTemplateOutlet="cadastralBody"></ng-container>
  </div>
} @else {
  <mat-expansion-panel
    class="!overflow-hidden !rounded-2xl !border !border-gray-6 !bg-surface-default !shadow-sm transition-all duration-200 hover:!border-gray-7 !before:hidden relative"
    [expanded]="expanded()"
    (expandedChange)="expanded.set($event)"
  >
    <!-- Subtle decorative ambient glow -->
    <div
      class="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-jade-a2 blur-3xl"
    ></div>

    <!-- Card Header -->
    <mat-expansion-panel-header
      class="!h-auto !py-4.5 !px-6 hover:!bg-gray-2/40 transition-colors [&_.mat-content]:!flex-row [&_.mat-content]:!items-center [&_.mat-content]:!justify-between [&_.mat-content]:!w-full [&_.mat-content]:!m-0 [&_.mat-content]:!overflow-visible"
    >
      <div class="flex items-center justify-between w-full gap-4 mr-3">
        <div class="flex items-center gap-3.5 min-w-0">
          <div
            class="h-11 w-11 flex items-center justify-center rounded-xl bg-jade-a2 text-jade-11 border border-jade-a4 shadow-xs shrink-0"
          >
            <mat-icon class="text-2xl leading-none">map</mat-icon>
          </div>
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <h4 class="text-base sm:text-lg font-bold text-gray-12 m-0">Données cadastrales</h4>
            </div>
            <p class="text-xs sm:text-sm text-gray-11 m-0 mt-0.5 truncate hidden sm:block">
              Identification foncière et contenance officielle de la parcelle.
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2.5 shrink-0">
          @if (hasData()) {
            <div
              class="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full border border-jade-a4 bg-jade-a2 text-jade-11 shadow-xs"
            >
              <mat-icon fontIcon="verified" class="text-sm leading-none shrink-0"></mat-icon>
              <span>
                @if (totalSurface(); as surf) {
                  {{ surf | number: '1.0-0' : 'fr-FR' }} m²
                } @else {
                  Identifié au cadastre
                }
              </span>
            </div>
          } @else {
            <div
              class="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium px-3 py-1 rounded-full border border-gray-6 bg-gray-2 text-gray-11 shadow-2xs"
            >
              <mat-icon fontIcon="info" class="text-xs leading-none shrink-0"></mat-icon>
              <span>Non renseigné</span>
            </div>
          }
        </div>
      </div>
    </mat-expansion-panel-header>

    <div class="px-6 pb-6 pt-3 border-t border-gray-5 space-y-6">
      <ng-container *ngTemplateOutlet="cadastralBody"></ng-container>
    </div>
  </mat-expansion-panel>
}

<ng-template #cadastralBody>
  <!-- Stat Blocks -->
  <div class="relative grid grid-cols-1 gap-4 sm:grid-cols-12">
    <!-- 1. Référence parcellaire -->
    <div
      class="rounded-xl border border-gray-6 bg-gray-2/80 p-5 flex flex-col justify-between backdrop-blur-xs sm:col-span-5"
    >
      <div>
        <div class="flex items-center justify-between text-sm font-medium text-gray-11 mb-1">
          <span>Parcelles cadastrales ({{ parcels().length }})</span>
          <mat-icon fontIcon="tag" class="text-sm leading-none text-gray-9"></mat-icon>
        </div>
        @if (parcels().length > 0) {
          <div class="mt-2 flex flex-wrap gap-1.5 max-h-36 overflow-y-auto pr-1">
            @for (p of parcels(); track p.reference) {
              <div
                class="inline-flex items-center gap-1.5 rounded-lg bg-surface-default px-2.5 py-1.5 border border-gray-5 shadow-2xs"
              >
                <span class="font-mono text-sm font-bold text-gray-12 select-all"
                  >{{ p.section }} {{ p.numero }}</span
                >
                @if (p.surface) {
                  <span class="text-xs text-gray-11">· {{ p.surface }} m²</span>
                }
                @if (p.isPrimary) {
                  <span
                    class="inline-flex items-center rounded-full bg-jade-a3 border border-jade-a5 px-1.5 py-0.2 text-2xs font-bold text-jade-11"
                  >
                    Principale
                  </span>
                }
                @if (p.hasBdnbBuilding === false) {
                  <span
                    class="inline-flex items-center rounded-full bg-amber-a3 border border-amber-a5 px-1.5 py-0.2 text-2xs font-semibold text-amber-11"
                  >
                    Non bâti
                  </span>
                }
              </div>
            }
          </div>
        } @else {
          <span class="mt-2 inline-block text-base font-semibold text-gray-11">Non renseignée</span>
        }
      </div>
      <p class="mt-3 text-xs sm:text-sm text-gray-11 m-0 leading-relaxed">
        Section et numéro d'ordre au plan parcellaire informatisé.
      </p>
    </div>

    <!-- 2. Superficie totale -->
    <div
      class="rounded-xl border border-gray-6 bg-gray-2/80 p-5 flex flex-col justify-between backdrop-blur-xs sm:col-span-3"
    >
      <div>
        <div class="flex items-center justify-between text-sm font-medium text-gray-11 mb-1">
          <span>Superficie totale</span>
          <mat-icon fontIcon="square_foot" class="text-sm leading-none text-gray-9"></mat-icon>
        </div>
        <div class="mt-2 flex items-baseline gap-1.5">
          @if (totalSurface(); as surface) {
            <span class="text-2xl sm:text-3xl font-black tracking-tight text-gray-12">
              {{ surface | number: '1.0-0' : 'fr-FR' }}
            </span>
            <span class="text-base font-bold text-gray-11">m²</span>
          } @else {
            <span class="text-base font-semibold text-gray-11">Non renseignée</span>
          }
        </div>
        @if (parcels().length > 1) {
          <span class="mt-1 block text-2xs text-gray-10">Cumul de {{ parcels().length }} parcelles</span>
        }
      </div>
      <p class="mt-3 text-xs sm:text-sm text-gray-11 m-0 leading-relaxed">
        Contenance cadastrale au sol (emprise foncière totale).
      </p>
    </div>

    <!-- 3. Localisation cadastrale -->
    <div
      class="rounded-xl border border-gray-6 bg-gray-2/80 p-5 flex flex-col justify-between backdrop-blur-xs sm:col-span-4"
    >
      <div>
        <div class="flex items-center justify-between text-sm font-medium text-gray-11 mb-1">
          <span>Adresse rattachée</span>
          <mat-icon fontIcon="place" class="text-sm leading-none text-gray-9"></mat-icon>
        </div>
        <div class="mt-2">
          <span class="text-sm font-semibold text-gray-12 line-clamp-2 leading-snug">
            {{ addressLabel() || 'Adresse en cours de géocodage' }}
          </span>
        </div>
      </div>
      @if (coordinates()?.latitude && coordinates()?.longitude) {
        <div class="mt-2 flex items-center gap-1.5 text-xs text-gray-11 font-mono">
          <span
            >GPS : {{ coordinates()?.latitude | number: '1.4-4' }},
            {{ coordinates()?.longitude | number: '1.4-4' }}</span
          >
        </div>
      } @else {
        <p class="mt-3 text-xs sm:text-sm text-gray-11 m-0 leading-relaxed">
          Pointage géographique automatique de la parcelle.
        </p>
      }
    </div>
  </div>

  <!-- Satellite Map with Parcels (Read-only Display) -->
  @if (hasCoordinates()) {
    <div class="space-y-2">
      <etxe-property-cadastral-map
        [lat]="coordinates()!.latitude!"
        [lng]="coordinates()!.longitude!"
        [selectedReferences]="selectedReferences()"
        [initialParcels]="initialParcelFeatures()"
        [interactive]="false"
      ></etxe-property-cadastral-map>
    </div>
  }

  <!-- Micro footer note -->
  <div class="flex items-center justify-between gap-3 text-xs text-gray-11 pt-2 border-t border-gray-5">
    <div class="flex items-center gap-1.5">
      <mat-icon fontIcon="shield" class="text-sm text-jade-11 leading-none"></mat-icon>
      <span>Plan cadastral informatisé sous licence ouverte Etalab / DGFiP & IGN.</span>
    </div>
  </div>
</ng-template>
